import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAIProcessing } from '../context/AIProcessingContext';
import { Loader2, Camera, CheckCircle, X, ArrowRight, Package, Sparkles, ChevronDown, ChevronUp, AlertCircle, Image } from 'lucide-react';

export const AIProgressBar = () => {
  const { isProcessing, progress, results, clearResults } = useAIProcessing();
  const location = useLocation();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Reset dismissed state when new processing starts
  useEffect(() => {
    if (isProcessing) {
      setIsDismissed(false);
    }
  }, [isProcessing]);

  // Don't show if dismissed
  if (isDismissed) {
    return null;
  }

  // Don't show if not processing and no pending results
  if (!isProcessing && (!results || results.length === 0)) {
    return null;
  }
  
  // Check if user is on the draft listings page
  const isOnDraftListingsPage = location.pathname === '/list/photos' || 
                                 location.pathname === '/add-inventory' ||
                                 location.pathname.startsWith('/list');
  
  // Don't show the full modal on draft listings page when complete
  if (!isProcessing && isOnDraftListingsPage && !isMinimized) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const percentage = progress.percentage || 0;

  // Phase-specific info
  const getPhaseInfo = (phase) => {
    switch (phase) {
      case 'starting':
        return { icon: Camera, color: '#C9956C', label: 'Preparing', showSpinner: true };
      case 'detecting':
        return { icon: Sparkles, color: '#C9956C', label: 'Phase 1: Detecting Items', showSpinner: true };
      case 'detection_complete':
        return { icon: CheckCircle, color: '#059669', label: 'Detection Complete', showSpinner: false };
      case 'assessing':
        return { icon: Loader2, color: '#C9956C', label: 'Phase 2: Analyzing Items', showSpinner: true };
      case 'complete':
        return { icon: CheckCircle, color: '#059669', label: 'Complete', showSpinner: false };
      default:
        return { icon: Loader2, color: '#C9956C', label: 'Processing', showSpinner: true };
    }
  };

  const phaseInfo = getPhaseInfo(progress.phase);
  const PhaseIcon = phaseInfo.icon;

  // Render photo status indicators
  const renderPhotoStatuses = () => {
    const statuses = progress.photoStatuses || {};
    const total = progress.total || 0;
    
    if (total === 0) return null;
    
    return (
      <div className="space-y-1 mt-2">
        {Array.from({ length: total }, (_, i) => {
          const status = statuses[i] || 'waiting';
          return (
            <div key={i} className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 flex items-center justify-center">
                {status === 'waiting' && (
                  <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                )}
                {status === 'processing' && (
                  <Loader2 className="w-3 h-3 text-[#C9956C] animate-spin" />
                )}
                {status === 'complete' && (
                  <CheckCircle className="w-3 h-3 text-[#059669]" />
                )}
                {status === 'failed' && (
                  <AlertCircle className="w-3 h-3 text-red-500" />
                )}
              </div>
              <span className={`${status === 'complete' ? 'text-[#059669]' : status === 'failed' ? 'text-red-500' : 'text-[var(--theme-text-secondary)]'}`}>
                Photo {i + 1}
                {status === 'complete' && progress.phase === 'detection_complete' && (
                  <span className="ml-1 opacity-70">
                    ({getItemsForPhoto(i)} items)
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Helper to get items count per photo (from results)
  const getItemsForPhoto = (photoIndex) => {
    if (!results) return '?';
    return results.filter(item => item.photo_index === photoIndex).length || '0';
  };

  return (
    <div data-testid="ai-progress-bar" className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-[60] pointer-events-auto pb-[env(safe-area-inset-bottom)]">
      <div className="bg-[var(--theme-card)] backdrop-blur-md rounded-2xl shadow-2xl border border-[var(--theme-border)] overflow-hidden transition-all duration-300">
        {isProcessing ? (
          isMinimized ? (
            /* Minimized State */
            <button
              onClick={() => setIsMinimized(false)}
              className="p-4 hover:bg-[var(--theme-hover)] transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#1E3A5F]/20 dark:bg-[#C9956C]/20 flex items-center justify-center">
                  <PhaseIcon className={`w-4 h-4 text-[var(--theme-text-primary)] ${phaseInfo.showSpinner ? 'animate-spin' : ''}`} />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-xs font-medium text-[var(--theme-text-primary)]">
                    {percentage}%
                  </span>
                  <ChevronUp className="w-3 h-3 text-[var(--theme-text-secondary)]" />
                </div>
              </div>
            </button>
          ) : (
            /* Full Processing State */
            <div className="p-4 w-[340px] sm:w-96">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1E3A5F]/20 dark:bg-[#C9956C]/20 flex items-center justify-center">
                    <PhaseIcon 
                      className={`w-5 h-5 ${phaseInfo.showSpinner ? 'animate-spin' : ''}`} 
                      style={{ color: phaseInfo.color }}
                    />
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold text-[var(--theme-text-primary)] text-sm">
                      {phaseInfo.label}
                    </span>
                    <div className="text-xs text-[var(--theme-text-secondary)] mt-0.5">
                      {progress.total} photo{progress.total > 1 ? 's' : ''} • Parallel processing
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1 hover:bg-[var(--theme-hover)] rounded-lg transition-colors"
                >
                  <ChevronDown className="w-4 h-4 text-[var(--theme-text-secondary)]" />
                </button>
              </div>
              
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs text-[var(--theme-text-secondary)] mb-1">
                  <span>{progress.message || 'Processing...'}</span>
                  <span className="font-semibold">{percentage}%</span>
                </div>
                <div className="h-2.5 bg-[var(--theme-border)] rounded-full overflow-hidden border border-[var(--theme-border)]">
                  <div 
                    className="h-full bg-gradient-to-r from-[#C9956C] to-[#E5B896] rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
              
              {/* Phase-specific content */}
              {(progress.phase === 'detecting' || progress.phase === 'detection_complete') && (
                <div data-testid="photo-detection-status" className="bg-[var(--theme-hover)] rounded-lg p-2 mb-3">
                  <div className="flex items-center gap-2 text-xs font-medium text-[var(--theme-text-primary)] mb-1">
                    <Image className="w-3 h-3" />
                    <span>Photo Detection Status</span>
                  </div>
                  {renderPhotoStatuses()}
                </div>
              )}
              
              {progress.phase === 'assessing' && (
                <div data-testid="item-assessing-status" className="bg-[var(--theme-hover)] rounded-lg p-2 mb-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[var(--theme-text-secondary)]">Analyzing items...</span>
                    <span className="font-medium text-[var(--theme-text-primary)]">
                      {progress.itemsAssessed || 0}/{progress.totalItems || progress.itemsToAssess || '?'}
                    </span>
                  </div>
                  <div className="h-1.5 bg-[var(--theme-border)] rounded-full overflow-hidden mt-1">
                    <div 
                      className="h-full bg-[#C9956C] rounded-full transition-all duration-300"
                      style={{ 
                        width: progress.itemsToAssess > 0 
                          ? `${(progress.itemsAssessed / progress.itemsToAssess) * 100}%` 
                          : '0%' 
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Items Found */}
              <div className="border-t border-[var(--theme-border)] pt-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-[#C9956C]" />
                    <span className="text-[var(--theme-text-secondary)]">
                      Items found: <span className="font-semibold text-[var(--theme-text-primary)]">{progress.totalItems || 0}</span>
                    </span>
                  </div>
                  {progress.failedPhotos > 0 && (
                    <span className="text-xs text-amber-500">
                      {progress.failedPhotos} photo{progress.failedPhotos > 1 ? 's' : ''} failed
                    </span>
                  )}
                </div>
              </div>
              
              <p className="text-xs text-[var(--theme-text-secondary)] opacity-70 mt-3 text-center">
                You can navigate away - we'll keep processing
              </p>
            </div>
          )
        ) : results && results.length > 0 ? (
          isMinimized ? (
            /* Minimized Complete State */
            <button
              onClick={() => setIsMinimized(false)}
              className="p-4 hover:bg-[var(--theme-hover)] transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#059669]/20 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-[#059669]" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-xs font-medium text-[var(--theme-text-primary)]">
                    {results.length} Ready
                  </span>
                  <ChevronUp className="w-3 h-3 text-[var(--theme-text-secondary)]" />
                </div>
              </div>
            </button>
          ) : (
            /* Results Ready State */
            <div className="w-[340px] sm:w-96">
              <div className="p-4 bg-[#059669]/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#059669]/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-[#059669]" />
                    </div>
                    <div className="flex-1">
                      <span className="font-semibold text-[var(--theme-text-primary)]">
                        Processing Complete!
                      </span>
                      <p className="text-xs text-[var(--theme-text-secondary)] mt-0.5">
                        {results.length} item{results.length > 1 ? 's' : ''} ready to review
                        {progress.successfulPhotos > 0 && (
                          <span className="ml-1">
                            from {progress.successfulPhotos} photo{progress.successfulPhotos > 1 ? 's' : ''}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setIsMinimized(true)}
                      className="p-1 hover:bg-[var(--theme-hover)] rounded-lg transition-colors"
                      title="Minimize"
                    >
                      <ChevronDown className="w-4 h-4 text-[var(--theme-text-secondary)]" />
                    </button>
                    <button
                      onClick={handleDismiss}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Dismiss"
                    >
                      <X className="w-4 h-4 text-[var(--theme-text-secondary)] hover:text-red-500" />
                    </button>
                  </div>
                </div>
                
                {progress.failedPhotos > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 mb-2 px-1">
                    <AlertCircle className="w-3 h-3" />
                    <span>{progress.failedPhotos} photo{progress.failedPhotos > 1 ? 's' : ''} couldn't be processed</span>
                  </div>
                )}
                
                {/* Only show Review Listings button if NOT on draft listings page */}
                {!isOnDraftListingsPage && (
                  <Link 
                    to="/list/photos"
                    className="block w-full mt-3 px-4 py-2 bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-lg font-medium text-sm transition-colors text-center"
                  >
                    Review Listings
                    <ArrowRight className="w-4 h-4 inline-block ml-2" />
                  </Link>
                )}
              </div>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};
